// Rejilla del calendario anual (septiembre-junio) + fusión con los eventos
// de la temporada. Ver CLAUDE.md: "calendario anual, inicio septiembre,
// finalización junio, con todos los domingos", igual que el PDF de
// referencia de la FMDS.
const MESES = [
  'ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO',
  'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE',
];

export const TIPOS_EVENTO = {
  FESTIVO: { etiqueta: 'Festivo', clase: 'cal-tipo-festivo' },
  EVENTO: { etiqueta: 'Evento', clase: 'cal-tipo-evento' },
  AVISO: { etiqueta: 'Aviso', clase: 'cal-tipo-aviso' },
};

// 'YYYY-MM-DD' -> Date a mediodía UTC, para no arrastrar el desfase de zona
// horaria al leer día/mes/año o comparar fechas.
const parsearFecha = (iso) => {
  const [anio, mes, dia] = iso.split('-').map(Number);
  return new Date(Date.UTC(anio, mes - 1, dia, 12));
};

const aIso = (fecha) => fecha.toISOString().slice(0, 10);

// El domingo que cierra la semana (lunes-domingo) de una fecha dada. Un
// domingo se devuelve a sí mismo; el resto de días avanza hasta el domingo
// siguiente (un sábado avanza 1 día, un lunes avanza 6, etc.).
const domingoDeSemana = (iso) => {
  const fecha = parsearFecha(iso);
  fecha.setUTCDate(fecha.getUTCDate() + ((7 - fecha.getUTCDay()) % 7));
  return aIso(fecha);
};

// Fecha de hoy en horario local (no UTC, para que coincida con lo que ve la
// usuaria en su zona horaria), como 'YYYY-MM-DD' comparable con las fechas
// de las filas.
export const hoyIso = () => {
  const ahora = new Date();
  const mes = String(ahora.getMonth() + 1).padStart(2, '0');
  const dia = String(ahora.getDate()).padStart(2, '0');
  return `${ahora.getFullYear()}-${mes}-${dia}`;
};

// "2026-2027" -> 2026. Si el id de temporada no tiene el formato esperado,
// usa el año en curso (calendario vacío en vez de romper la pantalla).
export const anioInicioTemporada = (temporadaId) => {
  const anio = parseInt(String(temporadaId).slice(0, 4), 10);
  return Number.isFinite(anio) ? anio : new Date().getFullYear();
};

// Todos los domingos de la temporada: de septiembre de `anioInicio` a junio
// de `anioInicio + 1`, ambos inclusive.
export const generarDomingosTemporada = (anioInicio) => {
  const inicio = new Date(Date.UTC(anioInicio, 8, 1, 12)); // 1 septiembre
  const fin = new Date(Date.UTC(anioInicio + 1, 5, 30, 12)); // 30 junio
  const cursor = new Date(inicio);
  cursor.setUTCDate(cursor.getUTCDate() + ((7 - cursor.getUTCDay()) % 7));

  const domingos = [];
  while (cursor <= fin) {
    domingos.push(aIso(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 7);
  }
  return domingos;
};

const formatearDia = (fechaInicio, fechaFin) => {
  const i = parsearFecha(fechaInicio);
  const f = parsearFecha(fechaFin);
  const anio = String(i.getUTCFullYear());
  const anioCorto = anio.slice(-2);

  if (fechaInicio === fechaFin) {
    const dia = String(i.getUTCDate());
    const mes = MESES[i.getUTCMonth()];
    return { dia, mes, anio, fechaCorta: `${dia.padStart(2, '0')}/${mes.slice(0, 3)}/${anioCorto}` };
  }
  const mismoMes = i.getUTCMonth() === f.getUTCMonth();
  const dia = `${i.getUTCDate()}-${f.getUTCDate()}`;
  const mesCorto = mismoMes
    ? MESES[i.getUTCMonth()].slice(0, 3)
    : `${MESES[i.getUTCMonth()].slice(0, 3)}/${MESES[f.getUTCMonth()].slice(0, 3)}`;
  return {
    dia,
    mes: mismoMes ? MESES[i.getUTCMonth()] : mesCorto,
    anio,
    // Formato unificado ("30/SEP/26") usado en la columna única de móvil.
    fechaCorta: `${dia.replace(/\d+/g, (n) => n.padStart(2, '0'))}/${mesCorto}/${anioCorto}`,
  };
};

// Une la rejilla fija de domingos (una fila por semana) con los eventos de
// la temporada: cada evento se fusiona en la fila del domingo que cierra su
// semana (el mismo domingo si es un evento de un día, o el domingo del fin
// de semana si es un puente sábado-domingo), en vez de crear una fila
// aparte que duplicaría visualmente la fecha. Un evento que caiga fuera del
// rango de domingos (p. ej. si se cuela una fecha de otro mes) se muestra
// en su propia fila, con su propia fecha, para no perder el dato.
//
// También marca `pasada` (la semana ya ha terminado) y `proxima` (la
// primera semana que todavía no ha terminado, para pintar la línea roja
// que se va desplazando fila a fila conforme pasan los domingos).
export const construirFilasCalendario = (domingos, eventos, hoy = hoyIso()) => {
  const filaPorDomingo = new Map(
    domingos.map((fecha) => [fecha, { key: `domingo-${fecha}`, domingo: fecha, eventos: [] }]),
  );
  const sueltos = [];

  eventos.forEach((e) => {
    const evento = {
      id: e.id,
      tipo: e.tipo,
      titulo: e.titulo,
      descripcion: e.descripcion,
      fechaInicio: e.fecha_inicio,
      fechaFin: e.fecha_fin,
      externo: e.externo,
    };
    const fila = filaPorDomingo.get(domingoDeSemana(e.fecha_fin));
    if (fila) {
      fila.eventos.push(evento);
    } else {
      sueltos.push({ key: `evento-${e.id}`, domingo: e.fecha_fin, eventos: [evento] });
    }
  });

  const filas = [...filaPorDomingo.values(), ...sueltos];
  filas.sort((a, b) => (a.domingo < b.domingo ? -1 : a.domingo > b.domingo ? 1 : 0));

  const indiceProximo = filas.findIndex((fila) => fila.domingo >= hoy);

  // Si la fila tiene un único evento, se muestra el rango de ese evento en
  // Día/Mes/Año (p. ej. "10-11" para un puente); con 0 o varios eventos se
  // muestra el domingo de la fila.
  return filas.map((fila, indice) => {
    const unico = fila.eventos.length === 1 ? fila.eventos[0] : null;
    const fechaInicio = unico ? unico.fechaInicio : fila.domingo;
    const fechaFin = unico ? unico.fechaFin : fila.domingo;
    return {
      ...fila,
      ...formatearDia(fechaInicio, fechaFin),
      pasada: indiceProximo === -1 ? true : indice < indiceProximo,
      proxima: indice === indiceProximo,
    };
  });
};
